import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import {
	optionalDateAsIsoString,
	optionalPositiveFloat,
	refineDateOrder,
	trimmedNullable,
} from "web_myplann/Shared/Validation/zodHelpers";
import { z } from "zod";

const getBusinessLoansBaseShape = (t: TranslateFunction) => ({
	name: trimmedNullable(
		z
			.string()
			.min(1, t(Resources.Portfolio.AddShared.Validations.required))
			.max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
	),
	contractNumber: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.Name,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.Name,
			}),
		),
	),
	producer: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	product: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	signedAt: optionalDateAsIsoString(),
	startAt: optionalDateAsIsoString(),
	endAt: optionalDateAsIsoString(),
});

const getBusinessLoansChangeShape = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return z.object({
		originalObligationAmount: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		installment: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		interestRate: z
			.number()
			.gt(0, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
			.nullish()
			.transform((val) => val ?? null),
		fixationEndDate: optionalDateAsIsoString(),
		paymentFrequency: z.nativeEnum(ClientPortfolioPaymentFrequencyEnum),
	});
};

const getDateOrderMessages = (t: TranslateFunction) => ({
	startBeforeSigned: t(
		Resources.Portfolio.AddShared.Validations.startBeforeSigned,
	),
	endBeforeSigned: t(Resources.Portfolio.AddShared.Validations.endBeforeSigned),
	endBeforeStart: t(Resources.Portfolio.AddShared.Validations.endBeforeStart),
	fixationEndBeforeStart: t(
		Resources.Portfolio.AddShared.Validations.fixationEndBeforeStart,
	),
});

export const getBusinessLoansCreateFormSchema = (t: TranslateFunction) =>
	z
		.object({
			...getBusinessLoansBaseShape(t),
			change: getBusinessLoansChangeShape(t),
		})
		.superRefine((data, ctx) =>
			refineDateOrder(getDateOrderMessages(t), ["change", "fixationEndDate"])(
				{
					signedAt: data.signedAt,
					startAt: data.startAt,
					endAt: data.endAt,
					fixationEndDate: data.change.fixationEndDate,
				},
				ctx,
			),
		);

export const getBusinessLoansEditFormSchema = (t: TranslateFunction) =>
	z
		.object(getBusinessLoansBaseShape(t))
		.superRefine((data, ctx) =>
			refineDateOrder(getDateOrderMessages(t))(data, ctx),
		);

export type BusinessLoansCreateFormInput = z.input<
	ReturnType<typeof getBusinessLoansCreateFormSchema>
>;
export type BusinessLoansCreateFormOutput = z.output<
	ReturnType<typeof getBusinessLoansCreateFormSchema>
>;

export type BusinessLoansEditFormInput = z.input<
	ReturnType<typeof getBusinessLoansEditFormSchema>
>;
export type BusinessLoansEditFormOutput = z.output<
	ReturnType<typeof getBusinessLoansEditFormSchema>
>;
